import type { WithActiveBranchId } from '@app/api/shared';
import type { NClients, NLeads, NSmallGlobalEntities, NTags } from '@app/types';

export enum ClientEndpoints {
  GetClientById = 'GetClientById',
  GetLeadClientForView = 'GetLeadClientForView',
  GetBranchesWithoutClient = 'GetBranchesWithoutClient',
  CopyClientToBranch = 'CopyClientToBranch',
  UpdateClient = 'UpdateClient',
  CreateClientByIntegration = 'CreateClientByIntegration',
  AddComment = 'AddComment',
  EditComment = 'EditComment',
  DeleteComment = 'DeleteComment',
  DeleteAvatar = 'DeleteAvatar',
  GetClientLeadInfo = 'GetClientLeadInfo',
  GetLeadCard = 'GetLeadCard',
  GetClientFieldValues = 'GetClientFieldValues',
  SetClientFieldValues = 'SetClientFieldValues',
  SetClientPhoneNumber = 'SetClientPhoneNumber',
  SetCompanyName = 'SetCompanyName',
}

export type GetClientByIdRequest = WithActiveBranchId<{
  clientId: number;
}>;

export type GetLeadClientForViewRequest = WithActiveBranchId<{
  leadId: number;
  forBranchId?: number;
}>;

export type GetBranchesWithoutClientRequest = WithActiveBranchId<{
  leadId: number;
}>;

export type CopyClientToBranchRequest = WithActiveBranchId<{
  data: {
    LeadId: number;
    BranchId: number;
  };
}>;

export interface ModelClientBranchItem {
  Id: number;
  IdS: string | null;
  Name: string | null;
}

export type GetClientByIdResponse = NClients.ModelClient;
export type GetLeadClientForViewResponse = NLeads.ModelLeadClientView;
export type GetBranchesWithoutClientResponse = ModelClientBranchItem[];
export type CopyClientToBranchResponse = number;

export interface UpdateClientPayload {
  Id: number;
  NameLast: string | null;
  NameFirst: string | null;
  NameMiddle: string | null;
  Gender: NSmallGlobalEntities.Gender;
  Email: string | null;
  Phone1: string | null;
  Phone1CountryId: number | null;
  Phone2: string | null;
  Phone2CountryId: number | null;
  DateBirth: string | null;
  IsPhoneVerified: boolean;
  Photo: string | null;
  LanguageId: number | null;
  ClientTags: NTags.ModelTag[] | null;
  Comment: string | null;
  CompanyName?: string | null;
  IsHuman: boolean | null;
  LeadId?: number | null;
  Snils: string | null;
  IdentityDocumentType: string | null;
  IdentityDocumentNumber: string | null;
  RegistrationAddress: string | null;
}

export type UpdateClientRequest = WithActiveBranchId<{
  data: UpdateClientPayload;
}>;

export type CreateClientByIntegrationRequest = WithActiveBranchId<{
  data: NClients.ModelClientLead;
}>;

export interface AddCommentPayload {
  ClientId: number;
  Comment: string;
}

export type AddCommentRequest = WithActiveBranchId<{
  data: AddCommentPayload;
}>;

export type EditCommentRequest = WithActiveBranchId<{
  commentId: number;
  text: string;
}>;

export type DeleteCommentRequest = WithActiveBranchId<{
  commentId: number;
}>;

export type UpdateClientResponse = NClients.ModelClient;
export type CreateClientByIntegrationResponse = NLeads.ModelLead;
export type AddCommentResponse = void;
export type EditCommentResponse = void;
export type DeleteCommentResponse = void;

export type DeleteAvatarRequest = WithActiveBranchId<{
  clientId: number;
}>;

export type GetClientLeadInfoRequest = WithActiveBranchId<{
  clientId: number;
}>;

export interface ModelClientLeadInfo {
  ClientId: number;
  LeadId: number;
  BranchId: number;
}

export type DeleteAvatarResponse = void;
export type GetClientLeadInfoResponse = ModelClientLeadInfo;

export type SetCompanyNameRequest = WithActiveBranchId<{
  clientId: number;
  companyName: string;
}>;
export type SetCompanyNameResponse = NClients.ModelClient;

export type GetLeadCardResponse = NLeads.ModelLeadClientView;
export type GetLeadCardRequest = WithActiveBranchId<{
  leadId: number;
  forBranchId?: number;
}>;

export interface ModelClientFieldValue {
  Id: number;
  FieldName: string | null;
  Value: string | null;
  ClientId: number;
  FieldId: number;
  /** Важное поле: показывается в окне записи и в карточке лида */
  IsImportant: boolean;
}

export type GetClientFieldValuesRequest = WithActiveBranchId<{
  clientId: number;
}>;

export interface SetClientFieldValuesPayload {
  ClientId: number;
  FieldValues: ModelClientFieldValue[];
}

export type SetClientFieldValuesRequest = WithActiveBranchId<{
  data: SetClientFieldValuesPayload;
}>;

export type GetClientFieldValuesResponse = ModelClientFieldValue[];
export type SetClientFieldValuesResponse = void;

export type SetClientPhoneNumberRequest = WithActiveBranchId<{
  clientId: number;
  phoneNumber: string;
}>;

export type SetClientPhoneNumberResponse = void;

/**
 * Комментарий клиента из ответа GetLeadCard. Timestamp приходит в UTC — в
 * отличие от строк истории действий, которые бэк уже перевёл в зону филиала.
 */
export interface ModelClientComment {
  Id: number;
  ClientId: number;
  Timestamp: string;
  UserId: number;
  UserName: string | null;
  Comment: string | null;
  // false — комментарий закреплён и висит сверху чата карточки, true — откреплён
  // кнопкой «Открепить» (POST /Client/Comments/Unpin, us/11229).
  IsUnpinned: boolean;
}
